import { useState, useContext, useEffect } from 'react';
import { ChevronDown } from '@styled-icons/feather';
import { KeyboardArrowDown, KeyboardArrowUp } from '@styled-icons/material';
import { useTheme } from 'next-themes';
import { DotFill } from '@styled-icons/octicons';
import mixpanel from 'mixpanel-browser';
import { errToString, ISpaceCustomization, RevalidateResponses, ISpace } from '@typedream/data';
import { toast } from 'react-toastify';
import useCurrentPagePath from '@/utils/useCurrentPagePath';
import { useRevalidateStatusStore } from '@/data/operation/store';
import { DARK_THEME, LIGHT_THEME, SPACE_SUBSCRIPTIONS } from '@/constants';
import { Button, Text, cn } from '@typedream/ui';
import { EVENT } from '@/analytics';
import useAllPublic from '@/utils/useAllPublic';
import { useGetSpaceQuery, usePublishMutation, useListPagesQuery } from '@/store';
import { useSpaceAndPageIDFromRouter } from '@/data/space/hooks';
import NewButton from '@/sharedComponents/v1/Components/Button';
import TagManager from 'react-gtm-module';
import sharedStyles from '../../styles/Shared.module.css';
import styles from './PublishButton.module.css';
import {
  CustomDomainActiveDispatchContext,
  CustomDomainPopupDispatchContext,
  CustomDomainPopupTriggerContext,
} from '../CustomDomainPopup/context';
import PublishedLinks from './PublishedLinks';
import PublishPages from './PublishPages';
import Dropdown from '../Dropdown';
import PulsatingCircle from '../PulsatingCircle';
import LoadingSmall from '../LoadingSmall';
import type { PublishSectionType } from './type';
import { STEP_MAP } from '../CustomDomainPopup/constants';

function PublishSection({
  open,
  setpublished,
  currentPageOnly,
  setcurrentPageOnly,
}: PublishSectionType) {
  const { theme } = useTheme();
  const [enableThemeToggle, setEnableThemeToggle] = useState(false);
  const [publishedTheme, setpublishedTheme] = useState(theme);
  const [openTheme, setopenTheme] = useState(false);
  const [totalPrivatePages, setTotalPrivatePages] = useState(0);

  const setactive = useContext(CustomDomainActiveDispatchContext);
  const setcustomDomainPopupOpen = useContext(CustomDomainPopupDispatchContext);
  const onAddCustomDomain = () => {
    mixpanel.track(EVENT.CLICK_ADD_CUSTOM_DOMAIN_BEFORE_PUBLISH_DROPDOWN);
    setactive(STEP_MAP.add_custom_domain.key);
    setcustomDomainPopupOpen(true);
  };

  const { status, checkStatus, resetStatus } = useRevalidateStatusStore();

  const { typedreamSubdomain } = useCurrentPagePath();

  const [spaceID, pageID] = useSpaceAndPageIDFromRouter();
  const { data: space } = useGetSpaceQuery([spaceID]);
  const { data: pages } = useListPagesQuery([spaceID]);

  useEffect(() => {
    const privatePages = pages && Object.entries(pages).filter(([key]) => !pages[key].public);
    setTotalPrivatePages(privatePages?.length);
  }, [pages]);

  useEffect(() => {
    setEnableThemeToggle(!!space?.customization?.enable_theme_toggle);
  }, [space?.customization?.enable_theme_toggle]);

  const { mutate: publish, isLoading: loadingPublishSpace } = usePublishMutation({
    fixedCacheKey: `publish-${spaceID}`,
  });

  const [allPublic, setAllPublic] = useAllPublic(spaceID);

  const triggerCustomDomainPopupOpen = useContext(CustomDomainPopupTriggerContext);

  const [advancedOpen, setadvancedOpen] = useState(false);
  const [isShownPrivateWarn, setIsShownPrivateWarn] = useState(false);

  const onClick = async () => {
    resetStatus();
    const customization: ISpaceCustomization = {
      ...space.customization,
      theme: publishedTheme === DARK_THEME ? 'dark' : 'light',
      enable_theme_toggle: enableThemeToggle,
    };
    let resp: {
      space: ISpace;
      revalidate_status: RevalidateResponses[];
    };

    try {
      resp = await publish({
        spaceID,
        customization,
        allPublic,
        pageIDs: [pageID],
        currentPageOnly,
        subscriptionPlanMap: SPACE_SUBSCRIPTIONS,
        typedreamURL: typedreamSubdomain,
        customDomain: space?.custom_domain,
      }).unwrap();

      // Send Event to Google Tag Manager
      TagManager.dataLayer({
        dataLayer: {
          event: 'publish',
          type: 'page',
        },
      });
    } catch (e) {
      setpublished(false);
      toast.error(errToString(e));
      return;
    }
    const revalidateStatus = resp.revalidate_status;

    mixpanel.track(EVENT.PUBLISH);

    const revStatus = await checkStatus(revalidateStatus);
    const subdomainRevalidateEndpoint = typedreamSubdomain.split('/')[0];
    const subdomainEndpoint = subdomainRevalidateEndpoint.includes('localhost')
      ? `http://${subdomainRevalidateEndpoint}`
      : `https://${subdomainRevalidateEndpoint}`;

    if (!space.custom_domain && revStatus.endpoints[subdomainEndpoint]?.status === 'succeeded') {
      triggerCustomDomainPopupOpen();
    }
  };

  const progressTotal =
    status?.endpoints &&
    Object.values(status?.endpoints)
      ?.filter((s) => s.status !== 'failed')
      ?.reduce((acc, val) => acc + val.publishPercent, 0);
  const progressCount =
    status?.endpoints &&
    Object.values(status?.endpoints)?.filter((s) => s.status !== 'failed')?.length;
  const succeedEndpoints =
    status?.endpoints &&
    Object.values(status?.endpoints)?.filter((s) => s.status === 'succeeded')?.length;

  useEffect(() => {
    setpublishedTheme(theme);
    setopenTheme(false);
  }, [open]);

  useEffect(() => {
    if (succeedEndpoints > 0) {
      setpublished(true);
    }
  }, [succeedEndpoints, setpublished]);

  return (
    <div className={styles.content}>
      {loadingPublishSpace && (
        <div className={styles.overlay}>
          <div className={cn('is-active')}>
            <div className="loader-box">
              <div className={cn(styles.loader, 'loader is-loading')} />
            </div>
          </div>
        </div>
      )}
      <PublishPages
        showTitle
        currentPageOnly={currentPageOnly}
        setcurrentPageOnly={setcurrentPageOnly}
      />
      <div className={cn(styles.item, '!flex-col !items-start !mt-2')}>
        <Text size="2xs" weight="medium" className="mb-1.5">
          Options
        </Text>
        <div className={cn(styles.advancedOptions, '!flex !flex-col !items-center !justify-start')}>
          <div
            role="none"
            style={{ cursor: 'pointer' }}
            onClick={() => setadvancedOpen((prevOpen) => !prevOpen)}
            className={cn(
              styles.advancedOptionsTrigger,
              '!flex !flex-row !items-center !justify-between'
            )}
          >
            <Text size="xs" weight="medium">
              Advanced Options
            </Text>
            <div>
              {advancedOpen ? (
                <KeyboardArrowUp size={20} color="var(--deprecated-neutral-700)" />
              ) : (
                <KeyboardArrowDown size={20} color="var(--deprecated-neutral-700)" />
              )}
            </div>
          </div>
          <div
            className={cn(styles.dropdown, {
              [styles.dropdownOpen]: advancedOpen,
              'mt-5': advancedOpen,
            })}
          >
            {advancedOpen && (
              <>
                <div className={cn(styles.item, 'pl-0 pr-0')}>
                  {isShownPrivateWarn && <PulsatingCircle className={styles.circleRight} small />}
                  <div className={`with-text-color is-size-7 ${isShownPrivateWarn && 'ml-2'}`}>
                    Make all pages public
                  </div>
                  <div className={cn(sharedStyles.switchContainer, '!flex')}>
                    <input
                      id="publish"
                      type="checkbox"
                      name="publish"
                      className={cn('switch is-rounded is-small is-primary')}
                      checked={allPublic}
                      onChange={(e) => setAllPublic(!allPublic)}
                      disabled={loadingPublishSpace}
                    />
                    <label htmlFor="publish">{/* empty label */}</label>
                  </div>
                </div>
                <div className={cn(styles.item, 'pl-0 pr-0')}>
                  <div className="with-text-color is-size-7">
                    Light/Dark Mode Toggle
                    <div
                      className="with-light-text-color"
                      style={{ lineHeight: '1em', fontSize: '0.6rem' }}
                    >
                      Allow visitors to change site appearance
                    </div>
                  </div>
                  <div className={cn(sharedStyles.switchContainer, '!flex', '!self-start')}>
                    <input
                      id="theme-toggle"
                      type="checkbox"
                      name="theme-toggle"
                      className={cn('switch is-rounded is-small is-primary')}
                      checked={enableThemeToggle}
                      onChange={(e) => setEnableThemeToggle((prev) => !prev)}
                      disabled={loadingPublishSpace}
                    />
                    <label htmlFor="theme-toggle">{/* empty label */}</label>
                  </div>
                </div>

                <div className={cn(styles.item, 'pl-0 pr-0')}>
                  <div className="with-text-color is-size-7">
                    {enableThemeToggle ? 'Default' : 'Published'} Theme
                  </div>
                  <Dropdown
                    open={openTheme}
                    setopen={setopenTheme}
                    width={90}
                    trigger={
                      <span>
                        <DotFill size={13} className={styles.dot} />
                        {publishedTheme}
                        <ChevronDown size={15} style={{ marginLeft: 5 }} />
                      </span>
                    }
                    right
                    triggerClassname={cn(styles.themeTrigger, {
                      [styles.themeItemDark]: publishedTheme === DARK_THEME,
                      [styles.themeItemLight]: publishedTheme === LIGHT_THEME,
                    })}
                    contentClassname={styles.themeContent}
                    content={
                      <>
                        <div
                          role="none"
                          className={cn(styles.themeItem, styles.themeItemDark)}
                          onClick={() => {
                            setopenTheme(false);
                            setpublishedTheme(DARK_THEME);
                          }}
                        >
                          <DotFill size={13} className={styles.dot} />
                          Dark
                        </div>
                        <div
                          role="none"
                          className={cn(styles.themeItem, styles.themeItemLight)}
                          onClick={() => {
                            setopenTheme(false);
                            setpublishedTheme(LIGHT_THEME);
                          }}
                        >
                          <DotFill size={13} className={styles.dot} />
                          Light
                        </div>
                      </>
                    }
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className={cn(styles.item, '!flex-col !items-start')}>
        {!!totalPrivatePages && (
          <div className="with-text-color is-size-7">
            You have {totalPrivatePages} page(s) private,
            {!advancedOpen && (
              <span
                role="none"
                className={styles.privatePage}
                onClick={() => {
                  setadvancedOpen(true);
                  setAllPublic(true);
                  setIsShownPrivateWarn(!!totalPrivatePages && true);
                }}
              >
                make these page(s) public
              </span>
            )}
          </div>
        )}
      </div>

      <div className={cn(styles.item, 'my-1')}>
        <div className={styles.divider} />
      </div>

      <PublishedLinks published={false} />

      <div className={cn(styles.itemColumn, styles.publishBtnContainer)} style={{ gap: 10 }}>
        {!space?.custom_domain && (
          <NewButton type="secondary" width="full" onClick={onAddCustomDomain}>
            Add Custom Domain
          </NewButton>
        )}

        <Button
          isLoading={loadingPublishSpace || status?.loading}
          className={styles.button}
          onClick={onClick}
        >
          Publish
        </Button>
      </div>

      {status?.loading && (
        <div className={cn(styles.item, '!justify-start')}>
          <LoadingSmall loading={status?.loading} className="mr-2" />
          <span className="is-subtitle is-size-7">
            Publishing
            {!Number.isNaN(progressCount) &&
              !Number.isNaN(progressTotal) &&
              progressCount > 0 &&
              ` (${progressTotal / (progressCount || 1)}%)`}
          </span>
        </div>
      )}
    </div>
  );
}

export default PublishSection;
